import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, Title } from 'chart.js';
import BadRecordingsTable from '../components/BadRecordingsTable';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './EQADashboard.css';
import Header from '../components/Header';
import config from '../config';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Title);

const EQADashboard = () => {
  const [data, setData] = useState({
    labels: [],
    newObjectsData: [],
    totalObjectsData: [],
    recordingsStartedData: [],
    recordingsUploadedData: [],
    totalRecordingsData: [],
    sumTotalObjects: 0,
    sumNewObjects: 0,
    startedRecordings: 0,
    uploadedRecordings: 0,
    totalRecordings: 0,
    totalStartedRecordings: 0,
    totalUploadedRecordings: 0,
  });

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [badRecordings, setBadRecordings] = useState([]);
  const [noDataMessage, setNoDataMessage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch EQA data from the backend API
        const eqaDataResponse = await fetch(config.api.eqaData);
        const eqaData = await eqaDataResponse.json();
        console.log('EQA Data:', eqaData);

        // Fetch bad recordings data from the backend API
        const badRecordingsResponse = await fetch(config.api.badRecordings);
        const badRecordingsData = await badRecordingsResponse.json();
        console.log('Bad Recordings Data:', badRecordingsData);

        // Fetch S3 data from the backend API
        const s3DataResponse = await fetch(config.api.s3Data);
        const s3Data = await s3DataResponse.json();
        console.log('S3 Data:', s3Data);

        // Fetch data from allRecordings.json
        const allRecordingsResponse = await fetch(config.api.allRecordings);
        const allRecordingsData = await allRecordingsResponse.json();
        console.log('All Recordings Data:', allRecordingsData);

        // Filter EQA data based on date range
        const filteredEQAData = eqaData.filter(entry => {
          const entryDate = new Date(entry.date);
          return (
            (!startDate || entryDate >= startDate) &&
            (!endDate || entryDate <= endDate)
          );
        });

        // Filter bad recordings based on date range
        const filteredBadRecordings = badRecordingsData.filter(entry => {
          const entryDate = new Date(entry.date.replace(/-/g, '/'));
          return (
            (!startDate || entryDate >= startDate) &&
            (!endDate || entryDate <= endDate)
          );
        });

        // Calculate total recordings for the selected date range
        const totalRecordingsData = filteredEQAData.reduce((acc, entry) => {
          const totalRecordings =
            entry.recordingSegments.segmentsStarted + entry.recordingSegments.segmentsUploaded;
          acc.push(totalRecordings);
          return acc;
        }, []);

        // Calculate total started and uploaded recordings for the selected date range
        const totalStartedRecordings = filteredEQAData.reduce(
          (sum, entry) => sum + entry.recordingSegments.segmentsStarted,
          0
        );

        const totalUploadedRecordings = filteredEQAData.reduce(
          (sum, entry) => sum + entry.recordingSegments.segmentsUploaded,
          0
        );

        // Check if there is data in the selected date range
        if (totalRecordingsData.length === 0) {
          setNoDataMessage('No data available for the selected date range. Adjust date range.');
          return;
        } else {
          setNoDataMessage(null);
        }

        setData({
          labels: filteredEQAData.map(entry => entry.date),
          newObjectsData: s3Data.map(entry => entry.newObjects),
          totalObjectsData: s3Data.map(entry => entry.totalObjects),
          recordingsStartedData: filteredEQAData.map(entry => entry.recordingSegments.segmentsStarted),
          recordingsUploadedData: filteredEQAData.map(entry => entry.recordingSegments.segmentsUploaded),
          totalRecordingsData,
          sumTotalObjects: s3Data[s3Data.length - 1]?.totalObjects || 0,
          sumNewObjects: s3Data[s3Data.length - 1]?.totalObjects - s3Data[0]?.totalObjects || 0,
          startedRecordings: totalStartedRecordings,
          uploadedRecordings: totalUploadedRecordings,
          totalRecordings: allRecordingsData
            .filter(entry => {
              const entryDate = new Date(entry.date);
              return (
                (!startDate || entryDate >= startDate) &&
                (!endDate || entryDate <= endDate)
              );
            })
            .reduce((acc, entry) => acc + entry.totalSessions, 0),
          totalStartedRecordings,
          totalUploadedRecordings,
        });

        setBadRecordings(filteredBadRecordings);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  return (
    <div className="Header">
      <Header />
      <h1 className="title">EQA Recording Dashboard</h1>
      <div className="date-range-picker">
        <DatePicker
          selected={startDate}
          onChange={date => setStartDate(date)}
          selectsStart
          startDate={startDate}
          endDate={endDate}
          placeholderText="Start Date"
        />
        <DatePicker
          selected={endDate}
          onChange={date => setEndDate(date)}
          selectsEnd
          startDate={startDate}
          endDate={endDate}
          placeholderText="End Date"
        />
      </div>
      <div className="dashboard-background">
        <div className="dashboard-container">
          {noDataMessage ? (
            <div className="widget">
              <p>{noDataMessage}</p>
            </div>
          ) : (
            <>
              <div className="widget">
                <h2>Objects in S3 Bucket</h2>
                <p>Total Objects: {data.sumTotalObjects}</p>
                <p>New Objects: {data.sumNewObjects}</p>
              </div>

              <div className="widget">
                <h2>Recordings</h2>
                <p>Total Recordings: {data.totalRecordings}</p>
                <p>Started: {data.totalStartedRecordings}</p>
                <p>Uploaded: {data.totalUploadedRecordings}</p>
                <p>Difference: {data.totalStartedRecordings - data.totalUploadedRecordings}</p>
              </div>

              <div className="graph-container">
                <h2>Objects in S3 Bucket</h2>
                <Line
                  data={{
                    labels: data.labels,
                    datasets: [
                      { data: data.totalObjectsData, label: 'Total Objects', borderColor: '#3498db', fill: false },
                    ],
                  }}
                  options={{
                    plugins: {
                      tooltip: {
                        enabled: true,
                        position: 'average',
                        mode: 'index',
                        intersect: false,
                      },
                      legend: {
                        display: true,
                      },
                    },
                    scales: {
                      x: {
                        display: true,
                      },
                      y: {
                        display: true,
                      },
                    },
                  }}
                />
              </div>

              <div className="graph-container">
                <h2>Recordings</h2>
                <Line
                  data={{
                    labels: data.labels,
                    datasets: [
                      { data: data.totalRecordingsData, label: 'Total Recordings', borderColor: '#8e44ad', fill: false },
                      { data: data.recordingsStartedData, label: 'Recordings Started', borderColor: '#2ecc71', fill: false },
                      { data: data.recordingsUploadedData, label: 'Recordings Uploaded', borderColor: '#f39c12', fill: false },
                    ],
                  }}
                  options={{
                    plugins: {
                      tooltip: {
                        enabled: true,
                        position: 'average',
                        mode: 'index',
                        intersect: false,
                      },
                      legend: {
                        display: true,
                      },
                    },
                    scales: {
                      x: {
                        display: true,
                      },
                      y: {
                        display: true,
                      },
                    },
                  }}
                />
              </div>

              <BadRecordingsTable badRecordings={badRecordings} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EQADashboard;
